"use client"

import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"

const TICKERS = ["btc", "eth", "sol", "near", "usdc", "usdt"]

const CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"

const TOTAL_LENGTH = 600
const TICKERS_PER_GEN = 2
const CYCLE_MS = 12000
type Segment = { text: string; isTicker: boolean }

function randomChar(): string {
  return CHARS[Math.floor(Math.random() * CHARS.length)]
}

function generateContent(): Segment[] {
  // Build one long random string
  const raw = Array.from({ length: TOTAL_LENGTH }, randomChar).join("")

  // Pick tickers to place
  const shuffled = [...TICKERS]
    .sort(() => Math.random() - 0.5)
    .slice(0, TICKERS_PER_GEN)

  // Place tickers at random positions with no overlaps, away from edges
  const placed: { col: number; ticker: string }[] = []

  for (const ticker of shuffled) {
    for (let attempt = 0; attempt < 200; attempt++) {
      const upperBound = Math.floor(TOTAL_LENGTH * (2 / 3))
      const col = Math.floor(Math.random() * (upperBound - ticker.length))

      const overlaps = placed.some(
        (p) =>
          col < p.col + p.ticker.length + 2 && col + ticker.length + 2 > p.col
      )

      if (!overlaps) {
        placed.push({ col, ticker })
        break
      }
    }
  }

  // Sort placements by position and build segments
  placed.sort((a, b) => a.col - b.col)

  const segments: Segment[] = []
  let cursor = 0

  for (const p of placed) {
    if (p.col > cursor) {
      segments.push({ text: raw.slice(cursor, p.col), isTicker: false })
    }
    segments.push({ text: p.ticker, isTicker: true })
    cursor = p.col + p.ticker.length
  }

  if (cursor < raw.length) {
    segments.push({ text: raw.slice(cursor), isTicker: false })
  }

  return segments
}

const Portfolio = () => {
  const [generation, setGeneration] = useState(0)
  const [segments, setSegments] = useState<Segment[]>([])
  const [highlighted, setHighlighted] = useState(false)

  useEffect(() => {
    setSegments(generateContent())

    let highlightTimer: ReturnType<typeof setTimeout>

    highlightTimer = setTimeout(() => setHighlighted(true), 1000)

    const interval = setInterval(() => {
      setHighlighted(false)
      setSegments(generateContent())
      setGeneration((g) => g + 1)
      highlightTimer = setTimeout(() => setHighlighted(true), 1000)
    }, CYCLE_MS)

    return () => {
      clearInterval(interval)
      clearTimeout(highlightTimer)
    }
  }, [])

  return (
    <div
      className="relative h-28 select-none overflow-hidden sm:h-28"
      aria-hidden="true"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={generation}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="break-all font-mono text-xs/none leading-none tracking-tight"
        >
          {segments.map((seg, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: static positional segments
              key={i}
              className={clsx(
                "before:-inset-y-px before:-inset-x-0.5 z-10 transition-colors duration-500 before:absolute before:block before:rounded before:transition-colors before:duration-500",
                seg.isTicker && "whitespace-nowrap",
                seg.isTicker && highlighted
                  ? "relative font-semibold text-green-500 before:bg-green-500/30"
                  : "text-white/25 before:bg-transparent"
              )}
            >
              <span className="relative">{seg.text}</span>
            </span>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default Portfolio
